// A debounced, trimmed search term.
//
// Every search box in the app is the same machine: the operator types, the
// value is trimmed, a term too short to match anything blanks out at once, and
// anything longer waits out a quiet period so the network is asked once per
// pause rather than once per keystroke. The raw box text stays the source — a
// local list beside the box may want every keystroke — so this publishes a
// second, settled term rather than replacing the box's own ref.
import { onScopeDispose, ref, watch, type Ref } from "vue";

export interface DebouncedTermOptions {
	/** Milliseconds of quiet before the term is published. */
	delay?: number;
	/** Trimmed terms shorter than this publish `""` immediately: a single
	 * character is not a search anyone meant to run, and waiting out the delay
	 * would leave the previous results under a box that no longer describes
	 * them. */
	minimumLength?: number;
}

export function useDebouncedTerm(
	source: Ref<string>,
	options: DebouncedTermOptions = {},
): Ref<string> {
	const delay = options.delay ?? 250;
	const minimumLength = options.minimumLength ?? 2;
	const term = ref("");
	let timer: ReturnType<typeof setTimeout> | null = null;

	watch(
		source,
		(value) => {
			if (timer !== null) {
				clearTimeout(timer);
				timer = null;
			}
			const trimmed = value.trim();
			if (trimmed.length < minimumLength) {
				term.value = "";
				return;
			}
			// Re-typing a space, or a keystroke that leaves the trimmed term
			// unchanged, must not re-run the search whose results are already on
			// screen.
			if (trimmed === term.value) return;
			timer = setTimeout(() => {
				timer = null;
				term.value = trimmed;
			}, delay);
		},
		{ immediate: true },
	);

	// A pane can be torn down between a keystroke and the timer firing; the late
	// write would then land on a ref nobody reads.
	onScopeDispose(() => {
		if (timer !== null) clearTimeout(timer);
	});

	return term;
}
